import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, EyeOff, Search, Compass, Trophy } from "lucide-react";
import { LINKS } from "../lib/constants";

const SectionLabel = ({ children, dark = false, onYellow = false }) => (
  <span className={`section-label ${dark ? "on-dark" : ""} ${onYellow ? "on-yellow" : ""}`}>{children}</span>
);

const Part = ({ num, title, icon: Icon, hookQ, intro, bullets, tools, dark = false }) => (
  <section className={`${dark ? "bg-brand-black text-white" : "bg-white"} py-20 lg:py-24 border-t-[1.5px] border-brand-black/15`} data-testid={`ftc-part-${num}`}>
    <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
      <div className="flex items-start gap-6 mb-6">
        <div className={`shrink-0 w-16 h-16 rounded-md ${dark ? "bg-brand-yellow" : "bg-brand-yellow"} flex items-center justify-center border-[1.5px] border-brand-black`}>
          <Icon size={28} className="text-brand-black" strokeWidth={2.2} />
        </div>
        <div>
          <p className={`text-xs uppercase tracking-[0.22em] font-bold ${dark ? "text-brand-yellow" : "text-brand-stone"}`}>Part {num}</p>
          <h2 className={`font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[0.92] uppercase ${dark ? "text-white" : ""}`}>{title}</h2>
        </div>
      </div>
      <p className={`font-display text-2xl sm:text-3xl normal-case leading-[1.35] uppercase mt-6 max-w-4xl ${dark ? "text-brand-yellow" : "text-brand-black"}`}>{hookQ}</p>
      <div className={`mt-8 space-y-5 text-[17px] leading-relaxed max-w-3xl ${dark ? "text-white/90" : "text-brand-ink"}`}>
        {intro.map((p, i) => <p key={i} dangerouslySetInnerHTML={{ __html: p }} />)}
      </div>
      <h4 className={`mt-10 font-display text-xl uppercase ${dark ? "text-white" : "text-brand-black"}`}>In this part of the method, you'll:</h4>
      <ul className={`mt-4 space-y-2.5 text-[16px] leading-relaxed max-w-3xl list-disc pl-6 ${dark ? "text-white/90" : "text-brand-ink"}`}>
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
      <p className={`mt-8 text-sm uppercase tracking-[0.14em] font-bold ${dark ? "text-brand-yellow" : "text-brand-stone"}`}>Tools for this part: {tools}</p>
    </div>
  </section>
);

export default function FollowTheCash() {
  return (
    <div data-testid="follow-the-cash-page">
      {/* HERO */}
      <section className="bg-brand-yellow border-b-[1.5px] border-brand-black pt-16 pb-20 lg:pt-20 lg:pb-24">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12 reveal">
          <SectionLabel onYellow>The Follow The Cash™ Method</SectionLabel>
          <h1 className="font-display text-[clamp(2.4rem,6vw,5.4rem)] mt-6 leading-[0.9]">
            Four moves from Profitable But Broke to <span className="hi-white">owning your future.</span>
          </h1>
          <div className="mt-9 space-y-5 text-lg lg:text-xl leading-relaxed text-brand-black max-w-4xl">
            <p>
              Most small business owners are making money but still can't sleep. They're{" "}
              <strong>Profitable But Broke</strong> — and they don't know why.
            </p>
            <p>
              Follow The Cash™ is the method that fixes that. Four parts. One journey. From
              avoidance to agency. No jargon. No shame. Just plain-English financial clarity that
              actually changes how you run your business.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" className="btn-primary shadow-w-default" data-testid="ftc-hero-reality-btn">
              Get Your Free Reality Check <ArrowUpRight size={18} />
            </a>
            <Link to="/work-with-me" className="btn-secondary" data-testid="ftc-hero-work-btn">
              See all ways to work with me
            </Link>
          </div>
        </div>
      </section>

      {/* PART 01 */}
      <Part
        num="01"
        title="Stop Hiding."
        icon={EyeOff}
        hookQ="What are you avoiding — and what's it costing you?"
        intro={[
          "Smart, capable business owners avoid their numbers. Not because they're irresponsible — because looking at the figures feels like standing in front of a jury.",
          "Shame, overwhelm and the belief that you \"should already know this\" keep you in a cycle: don't look → feel anxious → hide harder → crisis hits → temporary fix → repeat.",
          "But here's the truth: your numbers aren't judging you. They're informing you. A weather report doesn't criticise you for rain — it tells you to bring an umbrella.",
        ]}
        bullets={[
          "Understand the money avoidance trap and why it's not stupidity — it's shame",
          "Calculate the real cost of financial avoidance (late fees, surprise tax bills, missed opportunities, sleepless nights)",
          "Reframe your numbers as a tool, not a verdict",
          "Take the first step: look at your figures without dread",
        ]}
        tools={
          <>
            <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" className="underline decoration-brand-yellow decoration-2 underline-offset-2">The free Profitable-But-Broke Reality Check</a>
            {" · "}
            <Link to={LINKS.moneyFlowSession} className="underline decoration-brand-yellow decoration-2 underline-offset-2">The Money Flow Strategy Session</Link>
          </>
        }
      />

      {/* PART 02 */}
      <Part
        num="02"
        title="Start Knowing."
        icon={Search}
        hookQ="Why doesn't profit equal cash? What are the numbers really saying?"
        dark
        intro={[
          "You've made a profit. You feel successful. Then you check the bank account and nothing changed. What happened?",
          "<strong>Profit is on paper. Cash is oxygen.</strong> And the gap between them is where profitable businesses go broke.",
          "<em>Turnover is vanity. Profit is sanity. Cash is queen.</em>",
          "In this part, you stop nodding along to accounting jargon and start actually understanding what your numbers mean — in plain English, the way a detective reads clues, not the way an accountant reads a spreadsheet.",
        ]}
        bullets={[
          "Understand why revenue alone tells you almost nothing (turnover is vanity)",
          "Learn what profit actually means in practice — because the accounting definition and real-world experience rarely match up",
          "Grasp the difference between profit and cash, and why timing gaps kill profitable businesses",
          "Learn to read your P&L, balance sheet and cash flow statement without tears — knowing what to look for, not what to memorise",
        ]}
        tools={
          <>
            <Link to={LINKS.moneyFlowSession} className="underline decoration-brand-yellow decoration-2 underline-offset-2">The Money Flow Strategy Session</Link>
            {" · "}
            <Link to={LINKS.financialLeadership} className="underline decoration-brand-yellow decoration-2 underline-offset-2">ProfitMIND Advisory Programme</Link>
          </>
        }
      />

      {/* PART 03 */}
      <Part
        num="03"
        title="Take Control."
        icon={Compass}
        hookQ="How do you forecast without guessing? How do you make decisions with evidence?"
        intro={[
          "The power of forecasting will change your life! BUT often forecasts fail because people treat them as predictions. Wrong job for the tool.",
          "A forecast isn't a crystal ball — it's a planning device. A strategic planning device for your business. It's not about guessing the future accurately. It's about seeing your options clearly.",
          "This is where you move from understanding to action. You build a 12-month cash map. You run scenarios — best case, worst case, likely case. You turn numbers from something you react to into something you decide with.",
          "And it will answer all of your urgent questions: Can I hire? Can I pay myself more? Can I invest in a new studio space? All of it. It is that powerful.",
        ]}
        bullets={[
          "Demystify forecasting — why EVERY business needs one, not just big corporations",
          "Build a meaningful cashflow forecast (step by step, no accounting degree required)",
          "Use scenario thinking to make decisions: hiring, investing, cutting back, launching",
          "Establish simple daily, weekly, monthly and quarterly rhythms that keep you connected to your numbers without consuming your life",
        ]}
        tools={
          <>
            <a href={LINKS.forecastly} target="_blank" rel="noopener noreferrer" className="underline decoration-brand-yellow decoration-2 underline-offset-2">forecastly</a>
            {" · "}
            <Link to={LINKS.financialLeadership} className="underline decoration-brand-yellow decoration-2 underline-offset-2">ProfitMIND Advisory Programme</Link>
          </>
        }
      />

      {/* PART 04 */}
      <Part
        num="04"
        title="Own Your Future."
        icon={Trophy}
        hookQ="How do you make it stick — and plan for growth without running dry?"
        dark
        intro={[
          "You've stopped hiding. You know your numbers. You have a forecast. Now what?",
          "This is about building a business that lasts. Growth without strangling your cash flow. Resilience against the inevitable bumps — recessions, late payments, lost clients. Knowing when software is enough and when you need human expertise beside you.",
          "This is where <strong>Profitable But Broke</strong> becomes a thing of the past — permanently.",
        ]}
        bullets={[
          "Learn to scale without strangling your cash flow",
          "Build financial resilience: buffers, contingencies and stress-testing",
          "Know when you've outgrown DIY finance — and who to turn to (bookkeepers, accountants, advisors, fractional FDs, tools)",
          "Bring it all together: from fear to fluency, from avoidant to confident, from Profitable But Broke to Owning Your Future",
        ]}
        tools={
          <>
            <Link to={LINKS.financialLeadership} className="underline decoration-brand-yellow decoration-2 underline-offset-2">ProfitMIND Advisory Programme</Link>
            {" · "}
            <a href={LINKS.forecastly} target="_blank" rel="noopener noreferrer" className="underline decoration-brand-yellow decoration-2 underline-offset-2">forecastly (ongoing)</a>
          </>
        }
      />

      {/* WHERE ARE YOU */}
      <section className="bg-brand-yellow py-20 lg:py-28 border-t-[1.5px] border-brand-black" data-testid="ftc-where-are-you">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-12">
          <SectionLabel onYellow>Where are you in the journey?</SectionLabel>
          <h2 className="font-display text-[clamp(2.4rem,5.6vw,4.8rem)] mt-6 leading-[0.92]">
            Start where it hurts most.<br />
            You <span className="hi-white">don't</span> need to do all four parts today.
          </h2>
          <div className="mt-8 space-y-5 text-lg leading-relaxed text-brand-black max-w-4xl">
            <p>
              Most business owners are stuck somewhere between Part 1 and Part 3. The question
              isn't where you should be — it's where you actually are right now.
            </p>
            <p>
              The free Profitable-But-Broke Reality Check takes 3 minutes and tells you exactly
              which part of Follow The Cash™ Method you need most.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href={LINKS.freeAssessment} target="_blank" rel="noopener noreferrer" className="btn-primary shadow-w-default" data-testid="ftc-final-reality-btn">
              Get Your Free Reality Check <ArrowUpRight size={18} />
            </a>
            <Link to="/work-with-me" className="btn-secondary" data-testid="ftc-final-work-btn">
              See all ways to work with me
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
